import { useEffect } from 'react';

import {
  ContentClickEventObject,
  MouseMoveEvent,
  OnMouseMoveListener
} from '@strava/mre';

import { MAP_LAYERS } from '../../constants';
import { buildOnTapListener } from '../../helpers/buildOnTapListener';
import { useMRE } from '../../hooks/useMRE';
import { sanitizeActivityTypesParam } from './Segments.utils';

interface SegmentsProps {
  // All filter props are inserted into the tile download URLs as-is, no validation performed by the MRE.
  activityTypes: string | string[]; // type here matches return type of mapSegmentSportTypes in feature-maps package
  surfaceTypes: string;
  elevationFilter: string;
  distanceMin: string;
  distanceMax?: string;
  starred: boolean;
  top10: boolean;
  verified: boolean;
  creator: boolean;
  overall: boolean;
  selectedSegmentId?: bigint;
  onSegmentSelected?: (object: ContentClickEventObject | undefined) => void;
}

export const Segments = ({
  activityTypes,
  surfaceTypes,
  elevationFilter,
  distanceMin,
  distanceMax,
  starred,
  top10,
  verified,
  creator,
  overall,
  selectedSegmentId,
  onSegmentSelected
}: SegmentsProps) => {
  const { terrainEngine } = useMRE();

  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const segmentsFilter = terrainEngine.getContent().getSegmentsFilter();

    segmentsFilter.show({
      surfaceTypes,
      activityTypes: sanitizeActivityTypesParam(activityTypes),
      distanceMin,
      elevationFilter,
      distanceMax,
      starred,
      top10,
      verified,
      creator,
      overall
    });

    // We don't cleanup each time config changes. That would result in unnecessary
    // extra calls to the API.
  }, [
    terrainEngine,
    activityTypes,
    surfaceTypes,
    elevationFilter,
    distanceMin,
    distanceMax,
    starred,
    top10,
    verified,
    creator,
    overall
  ]);

  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const segmentsFilter = terrainEngine.getContent().getSegmentsFilter();
    return () => {
      // We do cleanup when the component unmounts.
      segmentsFilter.hide();
    };
  }, [terrainEngine]);

  // This effect handles segment hover
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const handleMouseMove: OnMouseMoveListener = {
      onMouseMove: (evt: MouseMoveEvent) => {
        const segmentObject = evt.objects.find((object) => {
          return object.layerName === MAP_LAYERS.segments;
        });

        void segmentObject; // added to prevent TS/lint error "declared but its value is never read"
        // TODO: MRE lacks a "hovered segment" style to apply here
        // TODO: might be nice to change cursor here, but it doesn't appear that Emscripten/SDL
        // is currently allowing that!
      }
    };

    terrainEngine.addOnMouseMoveListener(handleMouseMove);

    return () => {
      terrainEngine.removeOnMouseMoveListener(handleMouseMove);
    };
  }, [terrainEngine]);

  // This effect handles segment click
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const segmentsFilter = terrainEngine.getContent().getSegmentsFilter();

    const clearSegmentSelection = () => {
      segmentsFilter.unselectSegment();
      onSegmentSelected?.(undefined);
    };

    const onTapListener = buildOnTapListener({
      onMapTap: () => {
        // MapTap means by implication that there was no ContentTap
        clearSegmentSelection();
      },
      onContentTap: (evt) => {
        const segmentObject = evt.objects.find((object) => {
          return object.layerName === MAP_LAYERS.segments;
        });
        if (segmentObject) {
          // MRE enforces only one segment selected at a time; we don't need to deselect
          onSegmentSelected?.(segmentObject);
        } else {
          clearSegmentSelection();
        }
      }
    });

    terrainEngine.addOnTapListener(onTapListener);

    return () => {
      terrainEngine.removeOnTapListener(onTapListener);
    };
  }, [terrainEngine, onSegmentSelected]);

  useEffect(() => {
    if (terrainEngine && selectedSegmentId) {
      const segmentsFilter = terrainEngine.getContent().getSegmentsFilter();
      segmentsFilter.selectSegment(selectedSegmentId);

      return () => {
        segmentsFilter.unselectSegment();
      };
    }
  }, [terrainEngine, selectedSegmentId]);

  return null;
};
